/**
 * MapChrome
 * =========
 * Place at: frontend/src/components/map/MapChrome.jsx
 *
 * The minimum controls any map needs: zoom in, zoom out, reset view, and
 * a legend toggle.
 *
 * WHY THIS IS SEPARATE FROM MapControls
 * -------------------------------------
 * MapControls takes ~40 props. Search, zone filters, quick filters, type
 * and shape filters, sort order, starred projects, curated branches, the
 * lens, view mode, project and challenge fly-to handlers. Almost all of it
 * is main-map orchestration that a cohort map has no equivalent for —
 * mounting it there would mean stubbing thirty props with nulls to reach
 * the four buttons in its bottom-right corner.
 *
 * So the universally-needed cluster lives here. MapControls keeps the
 * main map's rich chrome; every other surface gets this.
 *
 * The markup is deliberately identical to the bottom-right block in
 * MapControls, so the two maps look the same rather than merely similar.
 * If you restyle one, restyle both — or better, make MapControls render
 * this component instead of its own copy.
 *
 * POSITIONING
 * -----------
 * Absolutely positioned, so the parent needs `position: relative`. The
 * default sits above a bottom nav; pass `className` to move it. The cohort
 * map has no bottom nav inside its container, so it passes its own.
 */

import React from 'react';
import { ZoomIn, ZoomOut, RotateCcw, HelpCircle } from 'lucide-react';
import { cn } from '../../utils/cn';

export const MapChrome = ({
  onZoomIn,
  onZoomOut,
  onReset,
  onToggleLegend,
  showLegend = false,
  className = 'absolute bottom-[60px] right-4 z-20',
}) => (
  <div className={cn('flex flex-col gap-2', className)}>
    <div className="flex flex-col bg-zinc-900/90 backdrop-blur-xl border border-zinc-800 rounded-xl overflow-hidden">
      <button onClick={onZoomIn} className="p-2.5 hover:bg-zinc-800 transition-colors" title="Zoom in">
        <ZoomIn className="w-4 h-4 text-zinc-400" />
      </button>
      <div className="h-px bg-zinc-800" />
      <button onClick={onZoomOut} className="p-2.5 hover:bg-zinc-800 transition-colors" title="Zoom out">
        <ZoomOut className="w-4 h-4 text-zinc-400" />
      </button>
      <div className="h-px bg-zinc-800" />
      <button onClick={onReset} className="p-2.5 hover:bg-zinc-800 transition-colors" title="Reset view">
        <RotateCcw className="w-4 h-4 text-zinc-400" />
      </button>
      {onToggleLegend && (
        <>
          <div className="h-px bg-zinc-800" />
          <button
            onClick={onToggleLegend}
            className={cn(
              'p-2.5 transition-colors',
              showLegend ? 'bg-purple-500/20 text-purple-400' : 'hover:bg-zinc-800 text-zinc-400'
            )}
            title="Map legend"
          >
            <HelpCircle className="w-4 h-4" />
          </button>
        </>
      )}
    </div>
  </div>
);

export default MapChrome;